/**
 * DataGrid - Virtualized table with TanStack Table + TanStack Virtual.
 * Features: sorting, column resize, pin left, quick filter, row selection, copy, CSV export.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type RowData,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Copy, Download } from 'lucide-react';
import { QuickFilter } from './QuickFilter';
import { DataGridLoading, DataGridEmpty, DataGridError } from './states';
import type { DataGridProps, DataGridDensity } from './types';
import { exportToCSV } from '@/utils/exportUtils';

const ROW_HEIGHT: Record<DataGridDensity, number> = {
  compact: 44,
  default: 56,
  comfortable: 68,
};

const PADDING_CLASS: Record<DataGridDensity, string> = {
  compact: 'px-3 py-2.5',
  default: 'px-4 py-3',
  comfortable: 'px-4 py-4',
};

const TEXT_CLASS: Record<DataGridDensity, string> = {
  compact: 'text-xs',
  default: 'text-sm',
  comfortable: 'text-sm',
};

function DataGridInner<T extends RowData>({
  data,
  columns,
  getRowId,
  density = 'compact',
  enableQuickFilter = true,
  quickFilterPlaceholder = 'Search...',
  enableRowSelection = false,
  selectedRowIds,
  onSelectedRowIdsChange,
  enableColumnResize = true,
  enableColumnPinning = true,
  pinnedLeftColumnIds,
  autoFitColumnIds,
  flexColumnId,
  columnSizing: columnSizingProp,
  onColumnSizingChange,
  enableSorting = true,
  enableCopy = true,
  enableCsvExport = true,
  cellTextOverflow = 'ellipsis',
  className,
  style,
  scrollContainerClassName,
  scrollContainerStyle,
  estimatedRowHeight,
  headerHeight: _headerHeight = 36,
  loading = false,
  error = null,
  emptyMessage = 'No data',
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange,
  renderRowDetail: _renderRowDetail,
  toolbar,
  onRowClick,
  getRowClassName,
  getFirstCellClassName,
  hideInternalToolbar = false,
  getToolbarActions,
  persistScrollKey,
}: DataGridProps<T>) {
  const [uncontrolledGlobalFilter, setUncontrolledGlobalFilter] = useState('');
  const globalFilterValue = controlledGlobalFilter ?? uncontrolledGlobalFilter;
  const setGlobalFilter = onGlobalFilterChange ?? setUncontrolledGlobalFilter;

  const [internalColumnSizing, setInternalColumnSizing] = useState<Record<string, number>>({});
  const columnSizing = columnSizingProp ?? internalColumnSizing;
  const setColumnSizing = onColumnSizingChange ?? setInternalColumnSizing;

  const densityKey = density || 'compact';
  const rowHeight = estimatedRowHeight ?? ROW_HEIGHT[densityKey];
  const paddingClass = PADDING_CLASS[densityKey];
  const textSizeClass = TEXT_CLASS[densityKey];

  const scrollRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const autoFitDoneRef = useRef(false);
  const scrollRestoredRef = useRef<string | null>(null);
  const persistScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollRestoreTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedScrollRef = useRef<{ scrollTop: number; scrollLeft: number } | null>(null);
  const prevPersistScrollKeyRef = useRef<string | undefined>(undefined);
  const toolbarHandlersRef = useRef({ onCopy: () => {}, onCsvExport: () => {} });

  const [, setScrollRestoreTick] = useState(0);

  const rowSelection = useMemo(() => {
    if (!selectedRowIds) return {};
    return Object.fromEntries([...selectedRowIds].map((id) => [id, true]));
  }, [selectedRowIds]);

  const table = useReactTable<T>({
    data,
    columns,
    getRowId,
    state: {
      globalFilter: globalFilterValue,
      rowSelection: enableRowSelection ? rowSelection : {},
      columnSizing,
      columnPinning: { left: pinnedLeftColumnIds ?? [] },
    },
    onColumnSizingChange: setColumnSizing,
    onGlobalFilterChange: setGlobalFilter,
    onRowSelectionChange: enableRowSelection
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(rowSelection) : {};
          onSelectedRowIdsChange?.(new Set(Object.keys(next).filter((k) => next[k])));
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableSorting,
    enableColumnResizing: enableColumnResize,
    columnResizeMode: 'onChange',
    enablePinning: enableColumnPinning,
    enableRowSelection,
  });

  const { rows } = table.getRowModel();
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  const handleCopy = useCallback(() => {
    const selected = table.getSelectedRowModel().rows;
    if (selected.length === 0) return;
    const cols = table.getAllLeafColumns().filter((c) => c.getCanHide() !== false);
    const header = cols.map((c) => (typeof c.columnDef.header === 'string' ? c.columnDef.header : c.id)).join('\t');
    const lines = selected.map((row) =>
      cols.map((col) => {
        const v = row.getValue(col.id);
        return v != null ? String(v).replace(/\t/g, ' ') : '';
      }).join('\t')
    );
    const text = [header, ...lines].join('\n');
    void navigator.clipboard.writeText(text);
  }, [table]);

  const handleCsvExport = useCallback(() => {
    const rowsToExport = table.getFilteredRowModel().rows;
    const cols = table.getAllLeafColumns().filter((c) => c.getCanHide() !== false);
    const exportRows = rowsToExport.map((row) => {
      const obj: Record<string, string | number> = {};
      cols.forEach((col) => {
        const header = typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
        const v = row.getValue(col.id);
        obj[header] = v != null ? String(v) : '';
      });
      return obj;
    });
    exportToCSV(exportRows, `export-${Date.now()}.csv`);
  }, [table]);

  toolbarHandlersRef.current.onCopy = handleCopy;
  toolbarHandlersRef.current.onCsvExport = handleCsvExport;
  useEffect(() => {
    getToolbarActions?.(toolbarHandlersRef.current);
  }, [getToolbarActions]);

  const copyCell = useCallback((value: unknown) => {
    const text = value != null ? String(value) : '';
    void navigator.clipboard.writeText(text);
  }, []);

  const copyFocusedRow = useCallback(
    (row: (typeof rows)[0]) => {
      const cols = table.getAllLeafColumns().filter((c) => c.getCanHide() !== false);
      const line = cols
        .map((col) => {
          const v = row.getValue(col.id);
          return v != null ? String(v).replace(/\t/g, ' ') : '';
        })
        .join('\t');
      void navigator.clipboard.writeText(line);
    },
    [table]
  );

  const showTable = !loading && !error && data.length > 0;
  const virtualItems = showTable ? virtualizer.getVirtualItems() : [];

  // Reset auto-fit only when context (e.g. project) changes, not on transient empty data.
  useEffect(() => {
    if (prevPersistScrollKeyRef.current !== persistScrollKey) {
      prevPersistScrollKeyRef.current = persistScrollKey;
      if (persistScrollKey != null) autoFitDoneRef.current = false;
    }
  }, [persistScrollKey]);

  // Auto-fit column width on first load for columns in autoFitColumnIds (run once when data is ready).
  useEffect(() => {
    if (!showTable || !autoFitColumnIds?.length || !measureRef.current || autoFitDoneRef.current) return;
    autoFitDoneRef.current = true;
    const el = measureRef.current;
    const updates: Record<string, number> = {};
    for (const columnId of autoFitColumnIds) {
      const col = table.getColumn(columnId);
      if (!col) continue;
      const minSize = col.columnDef.minSize ?? 80;
      const maxSize = col.columnDef.maxSize ?? 800;
      const headerText =
        typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
      const rowModel = table.getRowModel();
      const cellStrings = [
        headerText,
        ...rowModel.rows.map((row) => {
          const v = row.getValue(columnId);
          return v != null ? String(v) : '';
        }),
      ];
      let maxW = 0;
      for (const text of cellStrings) {
        el.textContent = text;
        maxW = Math.max(maxW, el.offsetWidth);
      }
      updates[columnId] = Math.round(Math.min(maxSize, Math.max(minSize, maxW + 32)));
    }
    if (Object.keys(updates).length > 0) {
      setColumnSizing((prev) => ({ ...prev, ...updates }));
    }
  }, [showTable, autoFitColumnIds, table, setColumnSizing]);

  // Restore scroll position when persistScrollKey is set and table has data (once per key per mount).
  // Only mark scrollRestoredRef when scroll actually took (content may not be ready on first apply). Re-apply at intervals.
  useEffect(() => {
    if (!persistScrollKey || !showTable || scrollRestoredRef.current === persistScrollKey) return;
    const key = `dataGrid.scroll.${persistScrollKey}`;
    let savedTop = 0;
    let savedLeft = 0;
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) {
        scrollRestoredRef.current = persistScrollKey;
        return;
      }
      const parsed = JSON.parse(raw) as { scrollTop?: number; scrollLeft?: number };
      savedTop = Math.max(0, parsed.scrollTop ?? 0);
      savedLeft = Math.max(0, parsed.scrollLeft ?? 0);
    } catch {
      scrollRestoredRef.current = persistScrollKey;
      return;
    }

    const el = scrollRef.current;
    if (!el) {
      const id = setTimeout(() => {
        if (scrollRef.current && scrollRestoredRef.current !== persistScrollKey) {
          const target = scrollRef.current;
          target.scrollTop = savedTop;
          target.scrollLeft = savedLeft;
          savedScrollRef.current = { scrollTop: savedTop, scrollLeft: savedLeft };
          scrollRestoredRef.current = persistScrollKey;
          setScrollRestoreTick((t) => t + 1);
        }
      }, 100);
      return () => clearTimeout(id);
    }

    const apply = () => {
      if (!el) return;
      el.scrollTop = savedTop;
      el.scrollLeft = savedLeft;
      const took = Math.abs(el.scrollTop - savedTop) <= 2 && Math.abs(el.scrollLeft - savedLeft) <= 2;
      if (took) {
        savedScrollRef.current = { scrollTop: savedTop, scrollLeft: savedLeft };
        scrollRestoredRef.current = persistScrollKey;
      }
    };

    apply();
    setScrollRestoreTick((t) => t + 1);

    requestAnimationFrame(() => {
      apply();
      requestAnimationFrame(apply);
    });

    const t1 = setTimeout(apply, 100);
    const t2 = setTimeout(apply, 300);
    const t3 = setTimeout(() => {
      apply();
      if (scrollRestoredRef.current !== persistScrollKey) {
        savedScrollRef.current = { scrollTop: savedTop, scrollLeft: savedLeft };
        scrollRestoredRef.current = persistScrollKey;
      }
    }, 500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      if (scrollRestoreTimeoutRef.current) {
        clearTimeout(scrollRestoreTimeoutRef.current);
        scrollRestoreTimeoutRef.current = null;
      }
    };
  }, [persistScrollKey, showTable]);

  // Persist scroll position on scroll (debounced). Always update savedScrollRef so unmount can flush it.
  const handleScroll = useCallback(() => {
    if (!persistScrollKey || !scrollRef.current) return;
    const el = scrollRef.current;
    savedScrollRef.current = { scrollTop: el.scrollTop, scrollLeft: el.scrollLeft };
    if (persistScrollTimeoutRef.current) clearTimeout(persistScrollTimeoutRef.current);
    persistScrollTimeoutRef.current = setTimeout(() => {
      persistScrollTimeoutRef.current = null;
      try {
        const key = `dataGrid.scroll.${persistScrollKey}`;
        const payload = savedScrollRef.current;
        if (payload) sessionStorage.setItem(key, JSON.stringify(payload));
      } catch {
        /* ignore */
      }
    }, 150);
  }, [persistScrollKey]);

  // On unmount, flush saved scroll from savedScrollRef (scrollRef.current is null in cleanup).
  useEffect(() => {
    if (!persistScrollKey) return;
    return () => {
      const payload = savedScrollRef.current;
      if (payload) {
        try {
          sessionStorage.setItem(
            `dataGrid.scroll.${persistScrollKey}`,
            JSON.stringify(payload)
          );
        } catch {
          /* ignore */
        }
      }
    };
  }, [persistScrollKey]);

  useEffect(() => {
    return () => {
      if (persistScrollTimeoutRef.current) clearTimeout(persistScrollTimeoutRef.current);
    };
  }, []);

  // Auto-fit column width on double-click resize handle (Excel-style).
  const handleAutoResizeColumn = useCallback(
    (columnId: string) => {
      const el = measureRef.current;
      const col = table.getColumn(columnId);
      if (!el || !col) return;
      const minSize = col.columnDef.minSize ?? 80;
      const maxSize = col.columnDef.maxSize ?? 800;
      const headerText =
        typeof col.columnDef.header === 'string' ? col.columnDef.header : col.id;
      const rowModel = table.getRowModel();
      const cellStrings = [
        headerText,
        ...rowModel.rows.map((row) => {
          const v = row.getValue(columnId);
          return v != null ? String(v) : '';
        }),
      ];
      let maxW = 0;
      for (const text of cellStrings) {
        el.textContent = text;
        maxW = Math.max(maxW, el.offsetWidth);
      }
      const width = Math.round(Math.min(maxSize, Math.max(minSize, maxW + 32)));
      setColumnSizing((prev) => ({ ...prev, [columnId]: width }));
    },
    [table, setColumnSizing]
  );

  // Column widths for CSS Grid: same fixed template for header and body so columns always align.
  // When flexColumnId is set, that column uses minmax(minSize, 1fr) so table fills viewport width.
  const headerGroup = showTable ? table.getHeaderGroups()[0] : null;
  const gridTemplateColumns = headerGroup
    ? headerGroup.headers
        .map((h) => {
          if (flexColumnId && h.id === flexColumnId) {
            const minSize = h.column.columnDef.minSize ?? 80;
            return `minmax(${minSize}px, 1fr)`;
          }
          return `${h.getSize()}px`;
        })
        .join(' ')
    : '';

  const useFlexLayout = Boolean(flexColumnId && headerGroup);
  // When flex: table is at least 100% (fills area) but grows to content width so horizontal scroll appears when many columns.
  const totalSize = table.getCenterTotalSize();
  const tableWidth = useFlexLayout ? `max(100%, ${totalSize}px)` : totalSize;
  const tableMinWidth = useFlexLayout ? '100%' : totalSize;

  return (
    <div
      className={cn('flex flex-col min-h-0 min-w-0 overflow-hidden', className)}
      style={{ height: '100%', ...style }}
      data-testid="data-grid"
    >
      {!hideInternalToolbar && (
        <div className="flex-none flex items-center gap-2 flex-wrap border-b border-border px-2 py-2">
          {toolbar}
          {enableQuickFilter && (
            <QuickFilter
              value={globalFilterValue}
              onChange={setGlobalFilter}
              placeholder={quickFilterPlaceholder}
              compact={densityKey === 'compact'}
            />
          )}
          {enableCopy && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              className="h-8 text-xs"
              title="Copy selected rows (or use Ctrl+C when row is focused)"
            >
              <Copy className="h-3.5 w-3.5 mr-1" />
              Copy
            </Button>
          )}
          {enableCsvExport && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleCsvExport}
              className="h-8 text-xs"
              title="Export CSV"
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              Export CSV
            </Button>
          )}
        </div>
      )}
      {/* Hidden element to measure text width for auto-fit column (same font/size as cells). */}
      <div
        ref={measureRef}
        className={cn(textSizeClass, paddingClass)}
        style={{
          position: 'absolute',
          left: -9999,
          top: 0,
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}
        aria-hidden
      />
      <div
        ref={scrollRef}
        onScroll={persistScrollKey ? handleScroll : undefined}
        className={cn('flex-1 min-w-0 min-h-0 overflow-auto overflow-x-auto overflow-y-auto border border-nb-border-subtle rounded-lg isolate', scrollContainerClassName)}
        style={{ maxWidth: '100%', width: '100%', ...scrollContainerStyle }}
      >
        {loading && <DataGridLoading className="min-h-full flex items-center justify-center" />}
        {error && <DataGridError className="min-h-full flex items-center justify-center" message={error} />}
        {!loading && !error && data.length === 0 && (
          <DataGridEmpty className="min-h-full flex items-center justify-center" message={emptyMessage} />
        )}
        {showTable && gridTemplateColumns && (
        <div
          role="table"
          className={cn(
            'border-collapse text-left',
            useFlexLayout ? 'block w-full' : 'inline-block min-w-0'
          )}
          style={{
            width: tableWidth,
            minWidth: tableMinWidth,
            boxSizing: 'border-box',
          }}
        >
          {/* Header row: sticky so it stays visible on vertical scroll; no overflow on table so sticky works */}
          <div
            role="rowgroup"
            className="sticky top-0 z-20 bg-muted border-b border-border"
          >
            <div
              role="row"
              className="grid border-border"
              style={{
                gridTemplateColumns,
                width: tableWidth,
                minWidth: useFlexLayout ? tableMinWidth : table.getCenterTotalSize(),
              }}
            >
              {headerGroup.headers.map((header) => {
                const isPinnedLeft = enableColumnPinning && header.column.getIsPinned() === 'left';
                let leftOffset = 0;
                if (isPinnedLeft) {
                  for (const h of headerGroup.headers) {
                    if (h.id === header.id) break;
                    if (h.column.getIsPinned() === 'left') leftOffset += h.getSize();
                  }
                }
                const pinStyles = isPinnedLeft
                  ? { position: 'sticky' as const, left: leftOffset, zIndex: 21 }
                  : {};
                const isFlexColumn = flexColumnId && header.id === flexColumnId;
                return (
                  <div
                    key={header.id}
                    role="columnheader"
                    className={cn(
                      paddingClass,
                      textSizeClass,
                      'font-medium text-foreground relative min-w-0 overflow-hidden border-b border-r border-border',
                      enableSorting && header.column.getCanSort() && 'cursor-pointer select-none hover:bg-muted/50',
                      isPinnedLeft && 'bg-muted shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]'
                    )}
                    style={{
                      ...(isFlexColumn ? { minWidth: header.column.columnDef.minSize ?? 80 } : {
                        width: header.getSize(),
                        minWidth: header.column.columnDef.minSize ?? 80,
                        maxWidth: header.column.columnDef.maxSize ?? header.getSize(),
                      }),
                      ...pinStyles,
                    }}
                    tabIndex={enableSorting && header.column.getCanSort() ? 0 : undefined}
                    onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                    onKeyDown={
                      enableSorting && header.column.getCanSort()
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              header.column.getToggleSortingHandler()?.(e as unknown as React.MouseEvent);
                            }
                          }
                        : undefined
                    }
                    aria-sort={
                      enableSorting && header.column.getIsSorted()
                        ? header.column.getIsSorted() === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : undefined
                    }
                  >
                    <div className="flex items-center gap-1 min-w-0 overflow-hidden">
                      <span className="min-w-0 truncate">
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {enableSorting && header.column.getIsSorted() && (
                        <span className="text-muted-foreground shrink-0">
                          {header.column.getIsSorted() === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </div>
                    {enableColumnResize && header.column.getCanResize() && (
                      <div
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize column"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          header.getResizeHandler()(e);
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          header.getResizeHandler()(e);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          handleAutoResizeColumn(header.column.id);
                        }}
                        className="absolute right-0 top-0 w-2 h-full cursor-col-resize hover:bg-primary/30 -mr-px select-none touch-none"
                        style={{ touchAction: 'none' }}
                        title="Drag to resize column; double-click to auto-fit width"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          {/* Body: virtualized rows, each row uses same grid template */}
          <div
            role="rowgroup"
            className="relative"
            style={{ height: `${virtualizer.getTotalSize()}px` }}
          >
            {virtualItems.map((virtualRow) => {
              const row = rows[virtualRow.index];
              if (!row) return null;
              const visibleCells = row.getVisibleCells();
              let isSelected = false;
              if (enableRowSelection) {
                try {
                  isSelected = row.getIsSelected() ?? false;
                } catch {
                  isSelected = false;
                }
              }
              return (
                <div
                  key={row.id}
                  ref={(el) => el && virtualizer.measureElement(el)}
                  role="row"
                  data-index={virtualRow.index}
                  data-state={isSelected ? 'selected' : undefined}
                  tabIndex={enableRowSelection || enableCopy ? 0 : undefined}
                  aria-selected={enableRowSelection ? isSelected : undefined}
                  className={cn(
                    'absolute left-0 right-0 grid border-b border-border hover:bg-muted/30 transition-colors',
                    isSelected && 'bg-primary/10',
                    onRowClick && 'cursor-pointer',
                    getRowClassName?.(row)
                  )}
                  style={{
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                    gridTemplateColumns,
                    width: useFlexLayout ? tableWidth : table.getCenterTotalSize(),
                    minWidth: useFlexLayout ? tableMinWidth : table.getCenterTotalSize(),
                  }}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      if (enableRowSelection) {
                        e.preventDefault();
                        row.toggleSelected();
                      }
                    } else if (
                      enableCopy &&
                      (e.ctrlKey || e.metaKey) &&
                      e.key === 'c' &&
                      e.target === e.currentTarget
                    ) {
                      e.preventDefault();
                      const selected = table.getSelectedRowModel().rows;
                      if (selected.length > 0) {
                        handleCopy();
                      } else {
                        copyFocusedRow(row);
                      }
                    }
                  }}
                >
                  {visibleCells.map((cell, cellIndex) => {
                    const isPinnedLeft = enableColumnPinning && cell.column.getIsPinned() === 'left';
                    const isFlexColumn = flexColumnId && cell.column.id === flexColumnId;
                    let cellLeftOffset = 0;
                    if (isPinnedLeft) {
                      for (const c of visibleCells) {
                        if (c.id === cell.id) break;
                        if (c.column.getIsPinned() === 'left') cellLeftOffset += c.column.getSize();
                      }
                    }
                    const cellPinStyles = isPinnedLeft
                      ? {
                          position: 'sticky' as const,
                          left: cellLeftOffset,
                          zIndex: 1,
                          boxShadow: '2px 0 4px -2px rgba(0,0,0,0.15)',
                        }
                      : {};
                    const cellValue = cell.getValue();
                    const titleText = cellValue != null ? String(cellValue) : '';
                    const isFirstCell = cellIndex === 0;
                    const firstCellClass = isFirstCell ? getFirstCellClassName?.(row) : '';
                    return (
                      <div
                        key={cell.id}
                        role="cell"
                        className={cn(
                          paddingClass,
                          textSizeClass,
                          'min-w-0 border-border',
                          cellTextOverflow === 'scroll' ? 'overflow-x-auto' : 'overflow-hidden',
                          isPinnedLeft && (isSelected ? 'bg-primary/10' : 'bg-muted'),
                          isPinnedLeft && 'shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]',
                          firstCellClass
                        )}
                        style={{
                          ...(isFlexColumn
                            ? { minWidth: cell.column.columnDef.minSize ?? 80 }
                            : {
                                width: cell.column.getSize(),
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize ?? cell.column.getSize(),
                              }),
                          ...cellPinStyles,
                        }}
                        onCopy={enableCopy ? (e) => { e.preventDefault(); copyCell(cell.getValue()); } : undefined}
                        title={enableCopy ? (titleText || 'Copy cell (Ctrl+C)') : (titleText || undefined)}
                      >
                        <span
                          className={cn(
                            'block min-w-0',
                            cellTextOverflow === 'scroll'
                              ? 'whitespace-nowrap overflow-x-auto max-w-full'
                              : 'truncate',
                          )}
                          title={titleText}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

export function DataGrid<T extends RowData>(props: DataGridProps<T>) {
  return <DataGridInner {...props} />;
}
